import { useState, useCallback, useEffect, useMemo, useRef, type RefObject } from "react"
import { Box, Typography } from "@mui/material"
import { isEqual } from "lodash"
import { OptimizeFormApi, useFormContext } from "connected-components/optimize-experiments/form-context"
import type { FactorRule } from "components/search-factor/types"
import type { MarketValues } from "../helpers/configuration"
import type { PositionSetInput, FactorRuleDisplayType, FactorRuleDisplay, FactorRuleField } from "./types"
import { useFilteredRules } from "./hooks/use-filtered-rules"
import FactorRules from "./display"
import FactorRulesMenu from "./menu"
import FactorRulesSearchModal from "./search-modal"
import { FACTOR_RULE_DISPLAY_TYPES } from "./constants"

interface FactorRulesWrapperProps {
  positionSet: PositionSetInput
  basePositionSet?: PositionSetInput | null
  portfolioMarketValues: MarketValues
  container?: HTMLDivElement | null
}

export default function FactorRulesWrapper({ positionSet, basePositionSet, portfolioMarketValues, container }: FactorRulesWrapperProps) {
  const form: OptimizeFormApi = useFormContext()
  const [ruleDisplayType, setRuleDisplayType] = useState<FactorRuleDisplayType | null>(null)
  const [ruleMenuAnchor, setRuleMenuAnchor] = useState<null | HTMLElement>(null)
  const isUpdatingExposureRulesRef = useRef(false)
  const isUpdatingCompositionRulesRef = useRef(false)

  const netMarketValue = portfolioMarketValues.longMarketValue - portfolioMarketValues.shortMarketValue

  const getRules = useCallback((ruleDisplayType: FactorRuleDisplayType): FactorRuleDisplay[] => {
    return form.state.values.rules[ruleDisplayType] || []
  }, [form])

  function addRule(rule: FactorRule, ruleDisplayType: FactorRuleDisplayType) {
    const updatedRules = [...getRules(ruleDisplayType), rule]
    form.setFieldValue(`rules.${ruleDisplayType}`, updatedRules)
  }

  function updateRule(rule: FactorRuleDisplay, field: FactorRuleField, value: string, ruleDisplayType: FactorRuleDisplayType) {
    const numericValue = parseFloat(value)
    const isComposition = ruleDisplayType === FACTOR_RULE_DISPLAY_TYPES.COMPOSITION

    const updatedRules = getRules(ruleDisplayType).map((existingRule) => {
      // Match by composite key (id + contentSetId if present)
      const isMatch = rule.contentSetId
        ? existingRule.id === rule.id && existingRule.contentSetId === rule.contentSetId
        : existingRule.id === rule.id

      if (!isMatch) return existingRule

      if (!isNaN(numericValue)) {
        const updated = { ...existingRule, [field]: numericValue }
        if (isComposition && netMarketValue !== null) {
          updated[`${field}EconomicExposure`] = (numericValue / 100) * netMarketValue
        }
        return updated
      } else {
        // Allows the user to clear the value by entering an empty string
        return {
          ...existingRule,
          [field]: undefined,
          ...(isComposition ? { [`${field}EconomicExposure`]: undefined } : {}),
        }
      }
    })

    form.setFieldValue(`rules.${ruleDisplayType}`, updatedRules)
  }

  function removeRule(rule: FactorRuleDisplay, ruleDisplayType: FactorRuleDisplayType) {
    const updatedRules = getRules(ruleDisplayType).filter(({ id, contentSetId }) => {
      if (rule.contentSetId) {
        return !(id === rule.id && contentSetId === rule.contentSetId)
      }
      return id !== rule.id
    })
    form.setFieldValue(`rules.${ruleDisplayType}`, updatedRules)
  }

  function toggleActiveValue(rule: FactorRuleDisplay, ruleDisplayType: FactorRuleDisplayType) {
    const updatedRules = getRules(ruleDisplayType).map((existingRule) => {
      // Match by composite key (id + contentSetId if present)
      const isMatch = rule.contentSetId
        ? existingRule.id === rule.id && existingRule.contentSetId === rule.contentSetId
        : existingRule.id === rule.id

      if (!isMatch) return existingRule

      return {
        ...existingRule,
        useActiveValue: !existingRule.useActiveValue,
      }
    })

    form.setFieldValue(`rules.${ruleDisplayType}`, updatedRules)
  }

  function handleOpenRuleSearch(ruleType: FactorRuleDisplayType) {
    setRuleDisplayType(ruleType)
    setRuleMenuAnchor(null)
  }

  const exposureRules = useMemo(() => form.state.values.rules.exposure || [], [form.state.values.rules.exposure])
  const compositionRules = useMemo(() => form.state.values.rules.composition || [], [form.state.values.rules.composition])

  const [filteredExposureRules, filteredCompositionRules, areExposureRulesReady, areCompositionRulesReady, exposureRulesError, compositionRulesError] = useFilteredRules(positionSet, basePositionSet, exposureRules, compositionRules)

  const setFieldValue = useCallback((ruleDisplayType: FactorRuleDisplayType, rules: FactorRuleDisplay[]) => {
    form.setFieldValue(`rules.${ruleDisplayType}`, rules)
  }, [form])

  const syncRules = useCallback((
    isUpdatingRulesRef: RefObject<boolean>,
    ruleType: FactorRuleDisplayType,
    filteredRules: FactorRuleDisplay[],
    currentRules: FactorRuleDisplay[],
    isReady: boolean,
  ) => {
    // Improve performance when rules are updated in quick succession
    if (isUpdatingRulesRef.current) return

    if (isReady && !isEqual(filteredRules, currentRules)) {
      isUpdatingRulesRef.current = true
      setFieldValue(ruleType, filteredRules)
      setTimeout(() => { isUpdatingRulesRef.current = false }, 0)
    }
  }, [setFieldValue])

  useEffect(() => {
    syncRules(isUpdatingExposureRulesRef, "exposure", filteredExposureRules, exposureRules, areExposureRulesReady)
  }, [syncRules, filteredExposureRules, exposureRules, areExposureRulesReady])

  useEffect(() => {
    syncRules(isUpdatingCompositionRulesRef, "composition", filteredCompositionRules, compositionRules, areCompositionRulesReady)
  }, [syncRules, filteredCompositionRules, compositionRules, areCompositionRulesReady])

  return (
    <Box className="components_factor-rules">
      <Typography className="factor-rules-label">
        Exposure and Composition Targets
      </Typography>
      <Typography variant="caption" className="factor-rules-description">
        Specify desired exposures to key risk or alpha factors to guide portfolio tilts.
      </Typography>
      <FactorRulesMenu
        anchorEl={ruleMenuAnchor}
        onOpen={(event) => setRuleMenuAnchor(event.currentTarget)}
        onClose={() => setRuleMenuAnchor(null)}
        onSelectType={handleOpenRuleSearch}
      />
      {areExposureRulesReady && areCompositionRulesReady && <FactorRules
        exposureError={exposureRulesError}
        compositionError={compositionRulesError}
        exposure={filteredExposureRules}
        composition={filteredCompositionRules}
        updateFactorRule={updateRule}
        removeFactorRule={removeRule}
        toggleActiveValue={toggleActiveValue}
        hasBase={Boolean(basePositionSet)}
      />}
      <FactorRulesSearchModal
        positionSet={positionSet}
        basePositionSet={basePositionSet}
        onRuleSelected={addRule}
        ruleDisplayType={ruleDisplayType || FACTOR_RULE_DISPLAY_TYPES.EXPOSURE}
        isOpen={ruleDisplayType !== null}
        onClose={() => setRuleDisplayType(null)}
        existingRules={ruleDisplayType ? form.state.values.rules[ruleDisplayType] || [] : []}
        container={container}
      />
    </Box>
  )
}
